import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  AlertTriangle, 
  Package, 
  Clock, 
  X,
  Mail,
  Bell
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const InventoryAlerts = ({ showPopup = false, onClose }) => {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const res = await api.get('/inventory/alerts');
      setAlerts(res.data);
    } catch (error) {
      console.error('Failed to fetch inventory alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      setSending(true);
      await api.post('/inventory/send-alert-email');
      toast.success('تم إرسال تنبيه المخزون بالبريد الإلكتروني');
    } catch (error) {
      toast.error('فشل إرسال البريد الإلكتروني');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!alerts || alerts.total_alerts === 0) {
    return null;
  }

  // Popup version (shows on login)
  if (showPopup) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="inventory-alert-popup">
        <div className="bg-card border border-white/10 rounded-xl p-6 max-w-md w-full animate-in fade-in zoom-in duration-300">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Bell className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="font-bold text-lg">تنبيهات المخزون</h3>
                <p className="text-sm text-muted-foreground">{alerts.total_alerts} تنبيه يتطلب انتباهك</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Low Stock Alerts */}
          {alerts.low_stock.count > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-yellow-500 mb-2 flex items-center gap-2">
                <Package className="w-4 h-4" />
                مواد منخفضة الكمية ({alerts.low_stock.count})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {alerts.low_stock.items.slice(0, 5).map((item) => (
                  <div key={item.id} className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-2 text-sm">
                    <span className="font-medium">{item.name}</span>
                    <span className="text-yellow-500 ms-2">
                      {item.quantity} {item.unit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expiring Alerts */}
          {alerts.expiring.count > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-red-500 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                مواد قاربت على الانتهاء ({alerts.expiring.count})
              </h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {alerts.expiring.items.slice(0, 5).map((item) => (
                  <div 
                    key={item.id} 
                    className={`border rounded-lg p-2 text-sm ${
                      item.days_remaining <= 0 
                        ? 'bg-red-500/20 border-red-500/30' 
                        : 'bg-orange-500/10 border-orange-500/20'
                    }`}
                  >
                    <span className="font-medium">{item.name}</span>
                    <span className={`ms-2 ${item.days_remaining <= 0 ? 'text-red-500' : 'text-orange-500'}`}>
                      {item.days_remaining <= 0 ? 'منتهي!' : `متبقي ${item.days_remaining} يوم`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={handleSendEmail}
              disabled={sending}
              className="flex-1 bg-primary text-primary-foreground gap-2"
            >
              <Mail className="w-4 h-4" />
              {sending ? 'جاري الإرسال...' : 'إرسال بالبريد'}
            </Button>
            <Button onClick={onClose} variant="outline" className="flex-1">
              إغلاق
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard widget version
  return (
    <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-xl p-4" data-testid="inventory-alert-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-yellow-500/20 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
          </div>
          <div>
            <h3 className="font-semibold">تنبيهات المخزون</h3>
            <p className="text-xs text-muted-foreground">{alerts.total_alerts} تنبيه</p>
          </div>
        </div>
        <Button
          onClick={handleSendEmail}
          disabled={sending}
          size="sm"
          variant="outline"
          className="gap-1 text-xs"
        >
          <Mail className="w-3 h-3" />
          إرسال تنبيه
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Low Stock */}
        <div className="bg-yellow-500/10 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Package className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium">كمية منخفضة</span>
          </div>
          <p className="text-2xl font-bold text-yellow-500">{alerts.low_stock.count}</p>
          {alerts.low_stock.items.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {alerts.low_stock.items[0].name}
              {alerts.low_stock.count > 1 && ` +${alerts.low_stock.count - 1}`}
            </p>
          )}
        </div>

        {/* Expiring */}
        <div className="bg-red-500/10 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium">قارب على الانتهاء</span>
          </div>
          <p className="text-2xl font-bold text-red-500">{alerts.expiring.count}</p>
          {alerts.expiring.items.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1 truncate">
              {alerts.expiring.items[0].name}
              {alerts.expiring.count > 1 && ` +${alerts.expiring.count - 1}`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryAlerts;
